import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';
import { parseIntOrDefault } from '@theme/utilities';

/**
 * A custom element that allows the user to select a quantity.
 *
 * The quantity is shown in a number input and can be picked from an attached
 * dropdown (<select>) listing the available quantities. Changes are broadcast via
 * QuantitySelectorUpdateEvent; parent components listen for these events and handle
 * them according to their needs.
 *
 * @typedef {Object} Refs
 * @property {HTMLInputElement} quantityInput
 * @property {HTMLDetailsElement} [quantitySelect]
 * @property {HTMLUListElement} [quantitySelectList]
 * @property {HTMLSelectElement} [quantitySelectNative]
 *
 * @extends {Component<Refs>}
 */
export class QuantitySelectorComponent extends Component {
  requiredRefs = ['quantityInput'];
  initialized = false;

  connectedCallback() {
    super.connectedCallback();

    this.initialized = true;
    this.syncSelection();

    this.handleOutsidePointer = (event) => {
      const details = this.refs.quantitySelect;
      if (details?.open && event.target instanceof Node && !details.contains(event.target)) {
        details.open = false;
      }
    };
    this.handleKeydown = (event) => {
      if (event.key !== 'Escape') return;
      const details = this.refs.quantitySelect;
      if (details?.open) {
        details.open = false;
        details.querySelector('summary')?.focus();
      }
    };

    document.addEventListener('pointerdown', this.handleOutsidePointer);
    this.addEventListener('keydown', this.handleKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.handleOutsidePointer) document.removeEventListener('pointerdown', this.handleOutsidePointer);
  }

  /**
   * When the dropdown opens, cap its height to the space available below the
   * control so it only scrolls when it genuinely runs out of room, then scroll
   * the selected option into view.
   * @param {Event} event
   */
  handleSelectToggle(event) {
    if (!(event.target instanceof HTMLDetailsElement) || !event.target.open) return;

    const list = this.refs.quantitySelectList;
    if (list) {
      const anchor = list.closest('.quantity-select-wrapper') ?? event.target;
      const available = window.innerHeight - anchor.getBoundingClientRect().bottom - 16;
      list.style.maxHeight = `${Math.max(120, available)}px`;
    }

    list?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }

  /**
   * Updates cart quantity and refreshes component state
   * @param {number} cartQty - The quantity currently in cart for this variant
   */
  setCartQuantity(cartQty) {
    this.refs.quantityInput.setAttribute('data-cart-quantity', cartQty.toString());
    this.updateCartQuantity();
  }

  /**
   * Checks if the current quantity can be added to cart without exceeding max
   * @returns {{canAdd: boolean, maxQuantity: number|null, cartQuantity: number, quantityToAdd: number}} Validation result
   */
  canAddToCart() {
    const { max, cartQuantity, value } = this.getCurrentValues();
    const quantityToAdd = value;
    const wouldExceedMax = max !== null && cartQuantity + quantityToAdd > max;

    return {
      canAdd: !wouldExceedMax,
      maxQuantity: max,
      cartQuantity,
      quantityToAdd,
    };
  }

  /**
   * Gets the current quantity value
   * @returns {string} The current value
   */
  getValue() {
    return this.refs.quantityInput.value;
  }

  /**
   * Sets the current quantity value
   * @param {string} value - The value to set
   */
  setValue(value) {
    this.refs.quantityInput.value = value;
    this.syncSelection();
  }

  /**
   * Updates min/max/step constraints and snaps value to valid increment
   * @param {string} min - Minimum value
   * @param {string|null} max - Maximum value (null if no max)
   * @param {string} step - Step increment
   */
  updateConstraints(min, max, step) {
    const { quantityInput } = this.refs;
    const currentValue = parseInt(quantityInput.value) || 0;

    quantityInput.min = min;
    if (max) {
      quantityInput.max = max;
    } else {
      quantityInput.removeAttribute('max');
    }
    quantityInput.step = step;

    const newMin = parseIntOrDefault(min, 1);
    const newStep = parseIntOrDefault(step, 1);
    const effectiveMax = this.getEffectiveMax();

    // Snap to valid increment if not already aligned
    let newValue = currentValue;
    if ((currentValue - newMin) % newStep !== 0) {
      // Snap DOWN to closest valid increment
      newValue = newMin + Math.floor((currentValue - newMin) / newStep) * newStep;
    }

    // Ensure value is within bounds
    newValue = Math.max(newMin, Math.min(effectiveMax ?? Infinity, newValue));

    if (newValue !== currentValue) {
      quantityInput.value = newValue.toString();
    }

    this.syncSelection();
  }

  /**
   * Gets current values from DOM (fresh read every time)
   * @returns {{min: number, max: number|null, step: number, value: number, cartQuantity: number}}
   */
  getCurrentValues() {
    const { quantityInput } = this.refs;

    return {
      min: parseIntOrDefault(quantityInput.min, 1),
      max: parseIntOrDefault(quantityInput.max, null),
      step: parseIntOrDefault(quantityInput.step, 1),
      value: parseIntOrDefault(quantityInput.value, 0),
      cartQuantity: parseIntOrDefault(quantityInput.getAttribute('data-cart-quantity'), 0),
    };
  }

  /**
   * Gets the effective maximum value for this quantity selector
   * Product page: max - cartQuantity (how many can be added)
   * Override in subclass for different behavior
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max, cartQuantity, min } = this.getCurrentValues();
    if (max === null) return null;
    // Product page: can only add what's left
    return Math.max(max - cartQuantity, min);
  }

  /**
   * Keeps the dropdown's selected option in sync with the current input value.
   */
  syncSelection() {
    const { quantitySelectList, quantitySelectNative, quantityInput } = this.refs;

    if (quantitySelectNative && quantitySelectNative.value !== quantityInput.value) {
      quantitySelectNative.value = quantityInput.value;
    }

    if (quantitySelectList) {
      for (const option of quantitySelectList.querySelectorAll('.quantity-select__option')) {
        const isSelected = option instanceof HTMLElement && option.dataset.value === quantityInput.value;
        option.setAttribute('aria-selected', isSelected ? 'true' : 'false');
      }
    }
  }

  /**
   * Handles picking a quantity. Works for both the native <select> (mobile) and
   * the custom option buttons (desktop).
   * @param {Event} event
   */
  selectQuantity(event) {
    let rawValue = null;
    if (event.target instanceof HTMLSelectElement) {
      rawValue = event.target.value;
    } else if (event.target instanceof Element) {
      const option = event.target.closest('.quantity-select__option');
      if (option instanceof HTMLElement) rawValue = option.dataset.value ?? null;
    }
    if (rawValue == null) return;

    const { min } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const quantity = Math.min(effectiveMax ?? Infinity, Math.max(min, parseInt(rawValue) || min));

    // No-op if the value hasn't changed, so picking the current quantity from the
    // dropdown doesn't fire a redundant cart update (and POST).
    const unchanged = quantity.toString() === this.refs.quantityInput.value;

    this.refs.quantityInput.value = quantity.toString();

    if (this.refs.quantitySelect) this.refs.quantitySelect.open = false;

    if (!unchanged) {
      this.onQuantityChange();
    }
    this.syncSelection();
  }

  /** @type {string | null} The input value when the field gained focus. */
  #focusValue = null;

  /**
   * When our input gets focused, we want to fully select the value.
   * @param {FocusEvent} event
   */
  selectInputValue(event) {
    const { quantityInput } = this.refs;
    // Remember the committed value so a blur with no change doesn't fire an update.
    this.#focusValue = quantityInput.value;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  /**
   * Handles the quantity set event (on blur).
   * Validates and snaps to valid values.
   * @param {Event} event - The event.
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    event.preventDefault();
    const { quantityInput } = this.refs;
    const { min, step } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Snap to bounds
    const quantity = Math.min(effectiveMax ?? Infinity, Math.max(min, parseInt(event.target.value) || 0));

    // Validate step increment
    if ((quantity - min) % step !== 0) {
      // Set the invalid value and trigger native HTML validation
      quantityInput.value = quantity.toString();
      quantityInput.reportValidity();
      return;
    }

    // Skip the update (and POST) when the value hasn't actually changed since focus.
    const unchanged = this.#focusValue != null && quantity.toString() === this.#focusValue;
    this.#focusValue = null;

    quantityInput.value = quantity.toString();
    if (!unchanged) {
      this.onQuantityChange();
    }
    this.syncSelection();
  }

  /**
   * Handles the quantity change event.
   */
  onQuantityChange() {
    const { quantityInput } = this.refs;
    const newValue = parseInt(quantityInput.value);

    this.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine) || undefined));
  }

  /**
   * Updates the cart quantity from data attribute and refreshes state
   * Called when cart is updated from external sources
   */
  updateCartQuantity() {
    const { quantityInput } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Clamp value to new effective max if necessary
    const clampedValue = Math.min(effectiveMax ?? Infinity, Math.max(min, value));

    if (clampedValue !== value) {
      quantityInput.value = clampedValue.toString();
    }

    this.syncSelection();
  }

  /**
   * Gets the quantity input.
   * @returns {HTMLInputElement} The quantity input.
   */
  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}
